/**
 * Button primitive (design system, FOUNDATION — v5 "PIXEL SWEETHEART").
 *
 * Squishy gummy buttons: press SQUASHES the button (scale + a 1px sink on a
 * juicy spring — springs.squish) while the CSS candy lip flattens; on
 * fine-pointer devices the button still leans a few px toward the cursor
 * (spring-returned on leave), and a soft ripple blooms from the touch point.
 * Game-layer variants (sr / gold / berserk) carry gradients + glows;
 * romance/safety variants stay matte. Same class + prop contract as v1-v4
 * (.btn / .btn--primary / …); `ripple={false}` opts out for dense lists.
 *
 * All motion is transform/opacity; MotionConfig reducedMotion="user" collapses
 * the spring work, and the magnet checks the media queries itself.
 */
import { useRef, useState, type PointerEvent, type ReactNode } from "react";
import { motion, useMotionValue, useSpring, type HTMLMotionProps } from "framer-motion";
import { prefersReducedMotion, springs } from "./motion.js";

export type ButtonVariant =
  | "default"
  | "primary"
  | "ghost"
  | "danger"
  | "like"
  | "reject"
  | "sr"
  | "gold"
  | "berserk";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends HTMLMotionProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  /** Press ripple from the touch point (default on). */
  ripple?: boolean;
  children?: ReactNode;
}

const MAGNET_RANGE = 4; // px of lean, max

function canMagnetize(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
    !prefersReducedMotion()
  );
}

interface RippleSpot {
  id: number;
  x: number;
  y: number;
}

export function Button({
  variant = "default",
  size = "md",
  block = false,
  loading = false,
  ripple = true,
  disabled,
  className,
  children,
  type = "button",
  onPointerMove,
  onPointerLeave,
  onPointerDown,
  style,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    variant !== "default" ? `btn--${variant}` : "",
    size !== "md" ? `btn--${size}` : "",
    block ? "btn--block" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  const inert = disabled || loading;

  // magnetic lean — spring-smoothed motion values, zero cost when idle
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 300, damping: 24 });
  const sy = useSpring(my, { stiffness: 300, damping: 24 });

  const [ripples, setRipples] = useState<RippleSpot[]>([]);
  const rippleId = useRef(0);

  function handlePointerMove(e: PointerEvent<HTMLButtonElement>) {
    onPointerMove?.(e);
    if (inert || !canMagnetize()) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2 * MAGNET_RANGE);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2 * MAGNET_RANGE);
  }

  function handlePointerLeave(e: PointerEvent<HTMLButtonElement>) {
    onPointerLeave?.(e);
    mx.set(0);
    my.set(0);
  }

  function handlePointerDown(e: PointerEvent<HTMLButtonElement>) {
    onPointerDown?.(e);
    if (inert || !ripple || prefersReducedMotion()) return;
    const r = e.currentTarget.getBoundingClientRect();
    const spot: RippleSpot = {
      id: rippleId.current++,
      x: e.clientX - r.left,
      y: e.clientY - r.top,
    };
    setRipples((s) => [...s.slice(-3), spot]);
    window.setTimeout(() => {
      setRipples((s) => s.filter((p) => p.id !== spot.id));
    }, 520);
  }

  return (
    <motion.button
      type={type}
      className={classes}
      disabled={inert}
      style={{ ...style, x: sx, y: sy }}
      whileTap={inert ? undefined : { scale: 0.93 }}
      transition={springs.squish}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onPointerDown={handlePointerDown}
      {...rest}
    >
      {loading && <span className="btn__spinner" aria-hidden="true" />}
      {children}
      {ripples.map((r) => (
        <span
          key={r.id}
          className="btn__ripple"
          style={{ left: r.x, top: r.y }}
          aria-hidden="true"
        />
      ))}
    </motion.button>
  );
}
